import {Button, Group, Modal, Stack, Switch, TextInput} from '@mantine/core'
import {useForm} from '@mantine/form'
import {useEffect, useState} from 'react'

import type {CreateRentalAssetTypeInput, RentalAssetType} from './schemas'

type RentalAssetTypeFormValues = {
    name: string
    isUniquelyIdentified: boolean
    hasUnitNumbers: boolean
}

type RentalAssetTypeFormModalProps = {
    opened: boolean
    onClose: () => void
    onCreate: (input: CreateRentalAssetTypeInput) => Promise<RentalAssetType>
    onSaved: () => void
}

export function RentalAssetTypeFormModal({
    opened,
    onClose,
    onCreate,
    onSaved,
}: RentalAssetTypeFormModalProps) {
    const [isSubmitting, setIsSubmitting] = useState(false)

    const form = useForm<RentalAssetTypeFormValues>({
        initialValues: {name: '', isUniquelyIdentified: true, hasUnitNumbers: true},
        validate: {
            name: (value) => (value.trim() ? null : 'Type name is required'),
        },
    })

    useEffect(() => {
        if (!opened) {
            return
        }
        form.setValues({name: '', isUniquelyIdentified: true, hasUnitNumbers: true})
        form.clearErrors()
        // eslint-disable-next-line react-hooks/exhaustive-deps -- reset modal fields when opened
    }, [opened])

    function handleClose() {
        form.reset()
        onClose()
    }

    async function handleSubmit(values: RentalAssetTypeFormValues) {
        setIsSubmitting(true)
        try {
            await onCreate({
                name: values.name.trim(),
                isUniquelyIdentified: values.isUniquelyIdentified,
                hasUnitNumbers: values.hasUnitNumbers,
            })
            onSaved()
            form.reset()
            onClose()
        } finally {
            setIsSubmitting(false)
        }
    }

    const trackingDescription = form.values.isUniquelyIdentified
        ? 'Equipment appears in the Equipment List with individual unit names.'
        : 'Equipment is tracked as an overall stock level by type (e.g. mats).'

    return (
        <Modal opened={opened} onClose={handleClose} title="Create equipment type" centered>
            <form onSubmit={form.onSubmit(handleSubmit)}>
                <Stack gap="md">
                    <TextInput label="Name" withAsterisk {...form.getInputProps('name')} />
                    <Switch
                        label="Track individual units?"
                        description={trackingDescription}
                        {...form.getInputProps('isUniquelyIdentified', {type: 'checkbox'})}
                    />
                    <Switch
                        label="Re-rented units have unit numbers"
                        description="Turn off for unnumbered items like hoses or mats."
                        {...form.getInputProps('hasUnitNumbers', {type: 'checkbox'})}
                    />

                    <Group justify="flex-end">
                        <Button type="button" variant="default" onClick={handleClose}>
                            Cancel
                        </Button>
                        <Button type="submit" loading={isSubmitting}>
                            Create type
                        </Button>
                    </Group>
                </Stack>
            </form>
        </Modal>
    )
}
